import React, { useMemo } from 'react';
import { Table, TableColumnType, Tag } from 'antd';
import {
  SubscriptionRecord,
  OrderStatus,
  SubscriptionType,
  MemberLevel,
  PaymentChannel,
  AndroidSubscriptionNotificationTypeLabelMap,
  AppleSubscriptionNotificationTypeLabelMap,
} from '../types';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import dayjs from 'dayjs';
import { FORMAT_TIME } from '@/types/common';
import {
  formatCampaign,
  formatChannel,
  formatRegisterPlatform,
} from '@/utils/attribution';

interface SubscriptionTableProps {
  data: SubscriptionRecord[];
  loading: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize?: number) => void;
  };
}

// 用户来源类型映射
const SourceTypeMap = new Map<number, string>([
  [1, 'suppart'],
  [2, 'appleId'],
  [3, 'facebook'],
  [4, 'googleId'],
  [5, 'LineId'],
]);

const formatNotificationType = (v: unknown, channel?: PaymentChannel): string => {
  if (v == null || v === '') return '-';
  // 苹果通知类型是字符串枚举（DID_RENEW / SUBSCRIBED 等）
  if (channel === PaymentChannel.APP_STORE) {
    const key = String(v);
    return AppleSubscriptionNotificationTypeLabelMap[key] ?? key;
  }
  // 安卓通知类型是数字枚举
  const num = typeof v === 'number' ? v : Number(v);
  if (Number.isFinite(num) && AndroidSubscriptionNotificationTypeLabelMap[num]) {
    return AndroidSubscriptionNotificationTypeLabelMap[num];
  }
  return String(v);
};

const SubscriptionTable: React.FC<SubscriptionTableProps> = ({
  data,
  loading,
  pagination,
}) => {
  const isMobile = useAtomValue(isMobileAtom);

  const columns = useMemo<TableColumnType<SubscriptionRecord>[]>(
    () => [
      {
        title: '交易ID',
        dataIndex: 'transactionId',
        key: 'transactionId',
        width: 180,
        // fixed: isMobile ? 'left' : undefined,
        render: (transactionId: string) => {
          return transactionId === '-' ? '-' : transactionId;
        },
      },
      {
        title: '订阅生效时间',
        dataIndex: 'subscriptionStartTime',
        key: 'subscriptionStartTime',
        width: 130,
        render: (time: string) => {
          if (!time) return '-';
          return dayjs(time).format(FORMAT_TIME);
        },
      },
      {
        title: '订阅到期时间',
        dataIndex: 'subscriptionEndTime',
        key: 'subscriptionEndTime',
        width: 130,
        render: (time: string) => {
          if (!time) return '-';
          return dayjs(time).format(FORMAT_TIME);
        },
      },
      {
        title: '支付金额',
        dataIndex: 'paymentAmount',
        key: 'paymentAmount',
      },
      {
        title: '币种',
        dataIndex: 'currency',
        key: 'currency',
      },
      {
        title: 'Supp ID',
        dataIndex: 'suppId',
        key: 'suppId',
      },
      {
        title: '用户名',
        dataIndex: 'username',
        key: 'username',
      },
      {
        title: '登录方式',
        dataIndex: 'sourceType',
        key: 'sourceType',
        render: (value: number) => {
            return <span>{value ? SourceTypeMap.get(value) || '-' : '-'}</span>;
        },
      },
      {
        title: '注册来源',
        dataIndex: 'deviceType',
        key: 'deviceType',
        render: (value: number) => formatRegisterPlatform(value),
      },
      {
        title: '注册时间',
        dataIndex: 'registerTime',
        key: 'registerTime',
        render: (time: string) => {
          if (!time) return '-';
          return dayjs(time).format(FORMAT_TIME);
        },
      },
      {
        title: '订阅类型',
        dataIndex: 'subscriptionType',
        key: 'subscriptionType',
        render: (type: SubscriptionType) => {
          const typeMap: Record<SubscriptionType, string> = {
            [SubscriptionType.MONTHLY]: '月付',
            [SubscriptionType.ANNUAL]: '半年付',
            [SubscriptionType.YEARLY]: '年付',
            [SubscriptionType.QUARTERLY]: '季度付',
            [SubscriptionType.TRIAL]: '试用',
          };
          return typeMap[type] || type;
        },
      },
      {
        title: '会员等级',
        dataIndex: 'memberLevel',
        key: 'memberLevel',
        render: (level: MemberLevel | number, record: SubscriptionRecord) => {
          // 安卓：按 productId 显示会员等级（覆盖 v1 老版 + E15 v2 全套产品 ID）
          if (record.paymentChannel === PaymentChannel.GOOGLE_PAY) {
            const pid = record.productId ?? '';
            // 当前有效 SKU 6 个：basic_monthly / standard_monthly / plus_monthly /
            // standard_yearly / plus_yearly / pro_yearly。其余为历史 SKU 防御性兜底
            if (pid === 'basic_monthly') return 'Basic';
            if (
              pid === 'standard_monthly' ||
              pid === 'standard_yearly' ||
              pid === 'standard_monthly_new' ||
              pid === 'standard_6monthly' ||
              pid === 'new_standard_monthly' ||
              pid === 'new_standard_yearly'
            ) {
              return 'Lite';
            }
            if (pid === 'plus_monthly' || pid === 'plus_yearly') return 'Pro';
            if (
              pid === 'pro_yearly' ||
              pid === 'pro_monthly' ||
              pid === 'pro_6monthly' ||
              pid === 'new_pro_monthly'
            ) {
              return 'Premium';
            }
            return '-';
          }
          // 苹果：0=Pro，1=Premium，2=Lite
          const levelNum = Number(level);
          if (levelNum === 2) return 'Lite';
          if (levelNum === 0 || level === MemberLevel.ADVANCED) return 'Pro';
          if (levelNum === 1 || level === MemberLevel.PREMIUM) return 'Premium';
          return '-';
        },
      },
      {
        title: '商品id',
        dataIndex: 'productId',
        key: 'productId',
        render: (v: string | undefined) => v ?? '-',
      },
      {
        title: '通知类型',
        dataIndex: 'notificationType',
        key: 'notificationType',
        render: (v: SubscriptionRecord['notificationType'], record: SubscriptionRecord) =>
          formatNotificationType(v, record.paymentChannel),
      },
      {
        title: '会员状态',
        dataIndex: 'orderStatus',
        key: 'orderStatus',
        render: (status: OrderStatus) => {
          const statusMap: Record<OrderStatus, { text: string; color: string }> = {
            [OrderStatus.ACTIVE]: { text: '有效', color: 'green' },
            [OrderStatus.EXPIRED]: { text: '过期', color: 'default' },
            [OrderStatus.CANCELLED]: { text: '撤销', color: 'red' },
          };
          const statusInfo = statusMap[status] || { text: String(status), color: 'default' };
          return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
        },
      },
      {
        title: '剩余天数',
        dataIndex: 'remainingDays',
        key: 'remainingDays',
        render: (days: number) => (days != null ? days : '已过期'),
      },
      {
        title: '是否续订',
        dataIndex: 'renewalStatus',
        key: 'renewalStatus',
        render: (renewalStatus: number) => {
          return renewalStatus === 1 ? '是' : '否';
        },
      },
      {
        title: '支付渠道',
        dataIndex: 'paymentChannel',
        key: 'paymentChannel',
        render: (channel: PaymentChannel) => {
          const channelMap: Record<PaymentChannel, string> = {
            [PaymentChannel.APP_STORE]: 'App Store',
            [PaymentChannel.GOOGLE_PAY]: 'Google Pay',
          };
          return channelMap[channel] || channel;
        },
      },
      {
        title: '渠道来源',
        dataIndex: 'afSource',
        key: 'afSource',
        render: (value: string, record: SubscriptionRecord) =>
          formatChannel(value, record.deviceType),
      },
      {
        title: '归因数据',
        dataIndex: 'afCampaign',
        key: 'afCampaign',
        // fixed: 'right' as const,
        render: (value: string) => formatCampaign(value),
      },
    ],
    [isMobile]
  );

  // 手机端只显示前三列
  // const columns = useMemo(() => {
  //   if (isMobile) {
  //     return allColumns.slice(0, 3);
  //   }
  //   return allColumns;
  // }, [allColumns, isMobile]);

  return (
    <Table<SubscriptionRecord>
      columns={columns}
      dataSource={data}
      rowKey={(record, index) => {
        // 如果 transactionId 是 '-'，使用 suppId 和 startTime 组合作为唯一 key
        if (record.transactionId === '-') {
          return `${record.suppId}-${record.subscriptionStartTime}-${index}`;
        }
        return `${record.transactionId}-${record.suppId}-${record.notificationType}-row-${index}` || `row-${index}`;
      }}
      loading={loading}
      scroll={{ x: 'max-content' }}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        onChange: pagination.onChange,
        showSizeChanger: false,
        showTotal: (total) => `共 ${total} 条`,
      }}
      size="small"
    />
  );
};

export default SubscriptionTable;

